import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@daater/api';
import { getCurrentAccountFromApiError } from './current-account-access';
import {
  acceptInvitationRequest,
  checkCompanyRequest,
  checkEmailRequest,
  confirmPaymentFromPayu,
  createPaymentOrder,
  deleteSavedReport,
  fetchCountries,
  fetchCurrentAccount,
  fetchCurrentTeam,
  fetchDatasets,
  fetchHistoricAnalytics,
  fetchPaymentHistory,
  fetchSavedReportsByDataset,
  fetchTableRecordsByQuery,
  fetchTableFields,
  fetchTableFilters,
  fetchTableRecords,
  fetchTablesByDataset,
  loginRequest,
  registerDemoRequest,
  registerUserRequest,
  requestPasswordRecovery,
  resetPassword,
  switchAccount,
  updateTerms,
  updateUserSettings,
  verifyInvitationRequest,
} from './endpoints';
import type { AcceptInvitationPayload, RegisterPayload, UpdateSettingsPayload } from './types';
import { useAccountStore, type Account } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';

export const queryKeys = {
  account: (token?: string | null) => ['account', token],
  currentTeam: ['current-team'],
  datasets: ['datasets'],
  tables: (datasetId?: string) => ['tables', datasetId],
  tableRecords: (
    tableId?: string,
    page?: number,
    query?: string,
    detailed?: boolean,
    demoMode?: boolean
  ) => ['table-records', tableId, page, query, detailed, demoMode],
  tableFields: (tableId?: string) => ['table-fields', tableId],
  tableFilters: (tableId?: string) => ['table-filters', tableId],
  historic: (userId?: string) => ['historic', userId],
  savedReports: (datasetId?: string) => ['saved-reports', datasetId],
  paymentHistory: (page = 1) => ['payment-history', page],
  countries: ['countries'],
  invitation: (token?: string) => ['invitation', token],
};

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: loginRequest,
  });
};

export const usePasswordRecoveryMutation = () => {
  return useMutation({
    mutationFn: requestPasswordRecovery,
  });
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
  });
};

export const useUpdateTermsMutation = () => {
  return useMutation({
    mutationFn: updateTerms,
  });
};

export const useCurrentAccountQuery = (token?: string | null) => {
  const setAccount = useAccountStore(state => state.setAccount);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const enabled = Boolean(token);

  const query = useQuery({
    queryKey: queryKeys.account(token),
    queryFn: fetchCurrentAccount,
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (query.data) {
      setAccount(query.data);
    }
  }, [query.data, setAccount]);

  useEffect(() => {
    if (query.error) {
      const accountFromError = getCurrentAccountFromApiError(query.error);
      if (accountFromError) {
        setAccount(accountFromError);
      }

      if (query.error instanceof ApiError && query.error.status === 401) {
        clearAuth();
      }
    }
  }, [query.error, clearAuth, setAccount]);

  return query;
};

export const useCurrentTeamQuery = (userId?: string) => {
  return useQuery({
    queryKey: queryKeys.currentTeam,
    queryFn: () => fetchCurrentTeam(userId as string),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useDatasetsQuery = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.datasets,
    queryFn: fetchDatasets,
    enabled,
  });
};

export const useTablesQuery = (datasetId?: string) => {
  return useQuery({
    queryKey: queryKeys.tables(datasetId),
    queryFn: () => fetchTablesByDataset(datasetId as string),
    enabled: Boolean(datasetId),
  });
};

export const useTableRecordsQuery = (
  tableId?: string,
  page = 1,
  query?: string,
  detailed = false,
  demoMode = false,
  searchTrigger = 0
) => {
  return useQuery({
    queryKey: queryKeys.tableRecords(tableId, page, query, detailed, demoMode),
    queryFn: () =>
      query
        ? fetchTableRecordsByQuery(tableId as string, query, page, detailed, demoMode)
        : fetchTableRecords(tableId as string, page, detailed, demoMode),
    enabled: Boolean(tableId),
    retry: false,
    staleTime: 60 * 60_000,
  });
};

export const useTableFieldsQuery = (tableId?: string) => {
  return useQuery({
    queryKey: queryKeys.tableFields(tableId),
    queryFn: () => fetchTableFields(tableId as string),
    enabled: Boolean(tableId),
  });
};

export const useTableFiltersQuery = (tableId?: string) => {
  return useQuery({
    queryKey: queryKeys.tableFilters(tableId),
    queryFn: () => fetchTableFilters(tableId as string),
    enabled: Boolean(tableId),
  });
};

export const useHistoricQuery = (userId?: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.historic(userId),
    queryFn: () => fetchHistoricAnalytics(userId as string),
    enabled: Boolean(userId) && enabled,
  });
};

export const useSavedReportsQuery = (datasetId?: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.savedReports(datasetId),
    queryFn: () => fetchSavedReportsByDataset(datasetId as string),
    enabled: Boolean(datasetId) && enabled,
  });
};

export const useSwitchAccountMutation = () => {
  const queryClient = useQueryClient();
  const account = useAccountStore(state => state.account);
  const setAccount = useAccountStore(state => state.setAccount);

  return useMutation({
    mutationFn: switchAccount,
    onSuccess: (_data, variables) => {
      const nextAccount: Account = {
        ...(account ?? {}),
        _id: variables.account,
        dataset: variables.dataset,
        table: variables.table,
      };
      setAccount(nextAccount);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      void queryClient.invalidateQueries({ queryKey: ['table-records'] });
    },
  });
};

export const useUpdateSettingsMutation = () => {
  return useMutation({
    mutationFn: (payload: UpdateSettingsPayload) => updateUserSettings(payload),
    // Note: We intentionally don't invalidate the account query here.
    // Column visibility is managed locally in the browser state.
    // Settings are persisted to backend and loaded on next page load.
  });
};

export const useDeleteSavedReportMutation = (datasetId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => deleteSavedReport(reportId),
    onSuccess: async () => {
      if (datasetId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.savedReports(datasetId) });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
      }
    },
  });
};

export const useCreatePaymentOrderMutation = () => {
  return useMutation({
    mutationFn: createPaymentOrder,
  });
};

export const useConfirmPaymentFromPayuMutation = () => {
  return useMutation({
    mutationFn: confirmPaymentFromPayu,
  });
};

export const usePaymentHistoryQuery = (page = 1) => {
  return useQuery({
    queryKey: queryKeys.paymentHistory(page),
    queryFn: () => fetchPaymentHistory(page),
  });
};

export const useCheckEmailMutation = () => {
  return useMutation({
    mutationFn: checkEmailRequest,
  });
};

export const useRegisterDemoMutation = () => {
  return useMutation({
    mutationFn: registerDemoRequest,
  });
};

export const useCheckCompanyMutation = () => {
  return useMutation({
    mutationFn: checkCompanyRequest,
  });
};

export const useRegisterUserMutation = () => {
  return useMutation({
    mutationFn: (payload: RegisterPayload) => registerUserRequest(payload),
  });
};

export const useCountriesQuery = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.countries,
    queryFn: fetchCountries,
    enabled,
  });
};

export const useVerifyInvitationQuery = (token?: string) => {
  return useQuery({
    queryKey: queryKeys.invitation(token),
    queryFn: () => verifyInvitationRequest(token as string),
    enabled: Boolean(token),
  });
};

export const useAcceptInvitationMutation = () => {
  return useMutation({
    mutationFn: (payload: AcceptInvitationPayload) => acceptInvitationRequest(payload),
  });
};
